import {useDispatch, useSelector} from "react-redux";
import React, {useEffect, useState} from "react";
import {TABLES} from "../../../../Apis/Tables";
import {DeleteItemsApi} from "../../../../reduxToolkit/Commons/CommonsApi";
import {MODELS} from "../../../../Apis/Models";
import {useNavigate} from "react-router-dom";
import {errorMsg} from "../../../../Apis/functions";
import {useTranslation} from "react-i18next";

/**
 * @email azapata@ibl.mx
 * @author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 *
 * TransportRequestApp component is the main module for handling transport request logic,
 * rendering table UI, managing modal dialogs for actions (accept, decline, comment, etc.),
 * handling search and pagination, and interacting with Redux state and backend APIs.
 *
 * Features:
 * - Displays a paginated, searchable table of transport requests.
 * - Renders dynamic action menus based on user roles and request statuses.
 * - Handles various user interactions such as accepting, rejecting, commenting, deleting.
 * - Shows expandable rows with detailed transport request information.
 * - Interacts with Redux for fetching, updating, and deleting data.
 *
 * State:
 * - Controls modals for accept/decline/assign/comment/detail.
 * - Manages search/filter and pagination states.
 * - Stores selected row data and expansion state.
 *
 * Hooks:
 * - useDispatch: dispatches Redux actions.
 * - useSelector: accesses Redux store state.
 * - useNavigate: navigates between routes.
 * - useEffect: handles lifecycle-based data loading and updates.
 *
 * Permissions:
 * - Menu options conditionally rendered based on supplier presence and user permissions.
 *
 * Dependencies:
 * - Redux slices: authentication, commons, search
 * - Constants: STATUS_MODEL, TABLES, MODELS, permissionObj
 * - UI Components: DropDownMenuProfile, DropDownMenuItem, CustomChip, etc.
 */

export const TransportRequestApp =() => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [supplierId, setSupplierId] = useState(sessionStorage.getItem('providerId'));
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAcceptedRequest, setShowAcceptRequest] = useState(false);
    const [showDeclineRequest, setShowDeclineRequest] = useState(false);
    const [showAssignEmployeesAndVehicles, setShowAssignEmployeesAndVehicles] = useState(false);
    const [showPreDepartureComment, setShowPreDepartureComment] = useState(false);
    const [showDepartureComment, setShowDepartureComment] = useState(false);
    const [showArrivalComment, setShowArrivalComment] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [showInTransitIssueComment, setShowInTransitIssueComment] = useState(false);
    const [showIssueComment, setShowIssueComment] = useState(false);
    const [selectedList, setSelectedList] = useState([]);
    const [row, setRow] = useState();
    const [searchElement, setSearchElement] = useState('');
    const [bodyForExport, setBodyForExport] = useState();
    const [search, setSearch] = useState(false);
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const moduleId = MODELS.LOGISTIC_REQUEST;
    const option = TABLES.LOGISTICREQUEST;

    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const [modified,setModified] = useState(false);
    const [filterDialogShow, setFilterDialogShow] = useState(false);
    const [finalArray, setFinalArray] = useState([]);
    const [criteria, setCriteria] = useState();
    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState([t('assigned_request'), t('open_request'), t('history')]);

    const deleteModal = (deleteItem) => {
        const tableName = option;
        const body = deleteItem;
        dispatch(DeleteItemsApi({ tableName, body })).then(({payload: {data: { success, code },},}) => {
                if(success){
                    setSelectedList([]);
                    setIsAllChecked(false)
                    setModified(!modified);
                }else
                    errorMsg(code);
            },
        );
    };


    const genericCommentModals = [
        {
            show: showPreDepartureComment,
            setter: setShowPreDepartureComment,
            title: 'pre_departure_comment',
            flagProp: { preIntransitComment: true },
        },
        {
            show: showDepartureComment,
            setter: setShowDepartureComment,
            title: 'departure_comment',
            flagProp: { IntransitComment: true },
        },
        {
            show: showInTransitIssueComment,
            setter: setShowInTransitIssueComment,
            title: 'in_transit_comment',
            flagProp: { inTransitIssueComment: true },
        },
        {
            show: showArrivalComment,
            setter: setShowArrivalComment,
            title: 'deliver_comment',
            flagProp: { deliverComment: true },
        },
        {
            show: showIssueComment,
            setter: setShowIssueComment,
            title: 'cancel_comment',
            flagProp: { cancelComment: true },
        },
    ];

    const commentModals = [
        {
            show: showAcceptedRequest,
            setter: setShowAcceptRequest,
            title: "accept_transport_request",
            flagProp: {},
        },
        {
            show: showDeclineRequest,
            setter: setShowDeclineRequest,
            title: "decline_transport_request",
            flagProp: { isDecline: true },
        },
        {
            show: showAssignEmployeesAndVehicles,
            setter: setShowAssignEmployeesAndVehicles,
            title: "assign_employee_vehicle",
            flagProp: { assign: true },
        },
        {
            show: showDetails,
            setter: setShowDetails,
            title: "request_summary",
            flagProp: { details: true },
        },
    ];

    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    useEffect(() => {
            setSearchElement('');
    }, [activeStep]);

    return {
        setShowDeleteModal,
        showDeleteModal,
        commentModals,
        genericCommentModals,
        row,
        DEV,
        permission,
        deleteModal,
        searchElement,
        setSearchElement,
        setBodyForExport,
        option,
        setSearch,
        simpleSearch,
        setSimpleSearch,
        supplierId,
        selectedList,
        setSelectedList,
        setModified,
        modified,
        navigate,
        t,
        search,
        moduleId,
        criteria,
        setCriteria,
        bodyForExport,
        filterDialogShow,
        setFilterDialogShow,
        setFinalArray,
        finalArray,
        setShowAcceptRequest,
        setShowDeclineRequest,
        setShowAssignEmployeesAndVehicles,
        setShowPreDepartureComment,
        setShowDepartureComment,
        setShowArrivalComment,
        setShowDetails,
        setShowInTransitIssueComment,
        setShowIssueComment,
        setRow,
        steps,
        activeStep,
        setActiveStep,
        handleStepChange,
        setIsAllChecked
    }
}
